import { FC, useState, useEffect } from 'react';
import { format, endOfMonth, startOfMonth, subMonths } from 'date-fns';
import { supabase } from '../../lib/supabase';
import type { ThirstyRecord } from '../../types/thirstyRecord';
import { useAuth } from '../../contexts/AuthContext';
import { useRole } from '../../hooks/useRole';

interface PayCycle {
  label: string;
  startDate: string;
  endDate: string;
}

interface EmployeeProfile {
  user_id: string;
  display_name: string;
}

const ThirstyRecords: FC = () => {
  const [records, setRecords] = useState<ThirstyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCycle, setSelectedCycle] = useState<string>('');
  const [payCycles, setPayCycles] = useState<PayCycle[]>([]);
  const [employeeFilter, setEmployeeFilter] = useState<string>('');
  const [employees, setEmployees] = useState<{ id: string; name: string }[]>([]);
  const { user } = useAuth();
  const { isAdmin, loading: roleLoading } = useRole();

  const generatePayCycles = () => {
    const cycles: PayCycle[] = [];
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const currentDay = currentDate.getDate();
    
    // Determine which half of the month we're in
    const isFirstHalf = currentDay <= 15;
    
    // Start with the current month's cycles
    let month = new Date(currentYear, currentMonth);
    
    for (let i = 0; i < 6; i++) {
      // For current month, only show cycles up to current period
      if (i === 0) {
        if (isFirstHalf) {
          // Only show 1-15 of current month
          const cycleStart = format(startOfMonth(month), 'yyyy-MM-dd');
          const cycleEnd = format(new Date(currentYear, currentMonth, 15), 'yyyy-MM-dd');
          const label = `${format(new Date(cycleStart), 'MMM d')} - ${format(new Date(cycleEnd), 'MMM d, yyyy')}`;
          cycles.push({ label, startDate: cycleStart, endDate: cycleEnd });
        } else {
          // Show both cycles of current month
          const firstHalfStart = format(startOfMonth(month), 'yyyy-MM-dd');
          const firstHalfEnd = format(new Date(currentYear, currentMonth, 15), 'yyyy-MM-dd');
          const secondHalfStart = format(new Date(currentYear, currentMonth, 16), 'yyyy-MM-dd');
          const secondHalfEnd = format(endOfMonth(month), 'yyyy-MM-dd');
          
          cycles.push({
            label: `${format(new Date(secondHalfStart), 'MMM d')} - ${format(new Date(secondHalfEnd), 'MMM d, yyyy')}`,
            startDate: secondHalfStart,
            endDate: secondHalfEnd
          });
          
          cycles.push({
            label: `${format(new Date(firstHalfStart), 'MMM d')} - ${format(new Date(firstHalfEnd), 'MMM d, yyyy')}`,
            startDate: firstHalfStart,
            endDate: firstHalfEnd
          });
        }
      } else {
        // Previous months - always show both cycles
        const firstHalfStart = format(startOfMonth(month), 'yyyy-MM-dd');
        const firstHalfEnd = format(new Date(month.getFullYear(), month.getMonth(), 15), 'yyyy-MM-dd');
        const secondHalfStart = format(new Date(month.getFullYear(), month.getMonth(), 16), 'yyyy-MM-dd');
        const secondHalfEnd = format(endOfMonth(month), 'yyyy-MM-dd');
        
        cycles.push({
          label: `${format(new Date(secondHalfStart), 'MMM d')} - ${format(new Date(secondHalfEnd), 'MMM d, yyyy')}`,
          startDate: secondHalfStart,
          endDate: secondHalfEnd
        });
        
        cycles.push({
          label: `${format(new Date(firstHalfStart), 'MMM d')} - ${format(new Date(firstHalfEnd), 'MMM d, yyyy')}`,
            startDate: firstHalfStart,
            endDate: firstHalfEnd
        });
      }
      
      // Move to previous month
      month = subMonths(month, 1);
    }

    return cycles;
  };

  useEffect(() => {
    const cycles = generatePayCycles();
    setPayCycles(cycles);
    if (cycles.length > 0) {
      setSelectedCycle(`${cycles[0].startDate},${cycles[0].endDate}`);
    }
  }, []);

  useEffect(() => {
    if (roleLoading) return;

    const loadEmployees = async () => {
      if (!isAdmin) {
        if (!user) {
          setEmployees([]);
          return;
        }

        const { data, error } = await supabase
          .from('user_profiles')
          .select('user_id, display_name')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!error && data) {
          setEmployees([{ id: data.user_id, name: data.display_name }]);
        } else {
          setEmployees([{ id: user.id, name: user.email || 'Your Profile' }]);
        }
        return;
      }

      const { data, error } = await supabase
        .from('user_profiles')
        .select('user_id, display_name')
        .order('display_name');

      if (!error && data) {
        setEmployees(data.map((profile: EmployeeProfile) => ({
          id: profile.user_id,
          name: profile.display_name
        })));
      }
    };

    loadEmployees();
  }, [isAdmin, roleLoading, user]);

  useEffect(() => {
    if (roleLoading) return;
    if (!isAdmin && user && employeeFilter !== user.id) {
      setEmployeeFilter(user.id);
    }
  }, [employeeFilter, isAdmin, roleLoading, user]);

  useEffect(() => {
    if (!selectedCycle || roleLoading) return;
    if (!isAdmin && (!user || !employeeFilter)) return;

    const loadRecords = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const [startDate, endDate] = selectedCycle.split(',');
        
        let query = supabase
          .from('thirsty_records')
          .select()
          .gte('record_date', startDate)
          .lte('record_date', endDate)
          .order('record_date', { ascending: false });

        const targetUserId = isAdmin ? employeeFilter : user?.id;
        if (targetUserId) {
          query = query.eq('user_id', targetUserId);
        }

        const { data, error } = await query;

        if (error) throw error;
        setRecords(data || []);
      } catch (err) {
        console.error('Failed to load records:', err);
        setError('Failed to load records. Please try again.');
        setRecords([]);
      } finally {
        setLoading(false);
      }
    };

    loadRecords();
  }, [selectedCycle, employeeFilter, isAdmin, roleLoading, user]);

  const handleExportCSV = () => {
    if (!records.length) return;

    const headers = [
      'Date',
      'Employee',
      'GCash Number',
      'Morning In',
      'Morning Out',
      'Afternoon In',
      'Afternoon Out',
      'Overtime In',
      'Overtime Out',
      'Total Hours',
      'Sales Amount',
      'CA Amount',
      'Comments'
    ];

    const csvContent = [
      headers.join(','),
      ...records.map(record => [
        format(new Date(record.record_date), 'MM/dd/yyyy'),
        record.display_name || 'Unknown',
        record.gcash_number || '',
        record.am_in || '',
        record.am_out || '',
        record.pm_in || '',
        record.pm_out || '',
        record.ot_in || '',
        record.ot_out || '',
        record.total_hours,
        record.sales_amount || '0',
        record.ca_amount || '0',
        (record.comments || '').replace(/,/g, ';') // Replace commas in comments
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `thirsty-records-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="bg-white p-8 rounded-lg shadow-lg mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Thirsty Records</h2>
          <button
            onClick={handleExportCSV}
            disabled={loading || records.length === 0}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export to CSV
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <label htmlFor="employee" className="block text-sm font-medium text-gray-700 mb-1">
              Employee
            </label>
            <select
              id="employee"
              value={employeeFilter}
              onChange={(e) => setEmployeeFilter(e.target.value)}
              disabled={!isAdmin}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100"
            >
              {isAdmin && <option value="">All Employees</option>}
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1">
            <label htmlFor="payCycle" className="block text-sm font-medium text-gray-700 mb-1">
              Pay Cycle
            </label>
            <select
              id="payCycle"
              value={selectedCycle}
              onChange={(e) => setSelectedCycle(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              {payCycles.map((cycle, index) => (
                <option key={index} value={`${cycle.startDate},${cycle.endDate}`}>
                  {cycle.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : records.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No records found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Morning</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Afternoon</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Overtime</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Hours</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sales</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CA</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comments</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {records.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {format(new Date(record.record_date), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{record.display_name}</div>
                      {record.gcash_number && (
                        <div className="text-sm text-gray-500">{record.gcash_number}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {record.am_in && record.am_out ? (
                        <span className="text-yellow-600">
                          {format(new Date(`2000-01-01T${record.am_in}`), 'h:mm a')} - 
                          {format(new Date(`2000-01-01T${record.am_out}`), 'h:mm a')}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {record.pm_in && record.pm_out ? (
                        <span className="text-orange-600">
                          {format(new Date(`2000-01-01T${record.pm_in}`), 'h:mm a')} - 
                          {format(new Date(`2000-01-01T${record.pm_out}`), 'h:mm a')}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {record.ot_in && record.ot_out ? (
                        <span className="text-blue-600">
                          {format(new Date(`2000-01-01T${record.ot_in}`), 'h:mm a')} - 
                          {format(new Date(`2000-01-01T${record.ot_out}`), 'h:mm a')}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-emerald-600">
                        {record.total_hours} hrs
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">
                        ₱{record.sales_amount?.toFixed(2) || '0.00'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">
                        ₱{record.ca_amount?.toFixed(2) || '0.00'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {record.comments || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThirstyRecords;